import React, { createContext, useContext, useState } from 'react';
import globalWindow from '@shared/core/globals';
import type { PrecomputedEmbeddingsBundle } from './precomputedEmbeddingsBundle.types';

/**
 * The theme-search index the server rendered a `style:*` gallery URL with (PRNT-5582).
 *
 * Typed here rather than on the global `Window` in typings/index.d.ts: any edit to that file makes
 * CI's storybook type-check recheck everything, which now runs out of memory. Move it there once
 * that check has the headroom.
 */
export type WindowWithThemeSearchIndex = { __INITIAL_THEME_SEARCH_INDEX__?: PrecomputedEmbeddingsBundle };

const ThemeSearchIndexContext = createContext<PrecomputedEmbeddingsBundle | undefined>(undefined);

type ThemeSearchIndexProviderProps = Readonly<{
  /** Server only: the bundle this request rendered with, so every render pass reads one copy. */
  initialThemeSearchIndex?: PrecomputedEmbeddingsBundle;
  children: React.ReactNode;
}>;

/**
 * Carries the server's theme-search index to the first client render.
 *
 * A server-rendered style URL filters its designs by the index's keyword names, so the client has
 * to have the same index *synchronously* on its first render or it reproduces a different grid
 * from the server's markup. `usePrecomputedEmbeddings` seeds itself from this, then replaces it
 * with the full bundle (vectors included) once its own effect resolves.
 *
 * Through context rather than the module-level singleton in `usePrecomputedEmbeddings`, because on
 * the server that singleton is per process and would leak one request's index into the next.
 * Empty on every other page — only URLs that need the index get it inlined.
 */
export const ThemeSearchIndexProvider = ({ initialThemeSearchIndex, children }: ThemeSearchIndexProviderProps) => {
  const [bundle] = useState(() => initialThemeSearchIndex ?? (globalWindow as typeof globalWindow & WindowWithThemeSearchIndex).__INITIAL_THEME_SEARCH_INDEX__);
  return <ThemeSearchIndexContext.Provider value={bundle}>{children}</ThemeSearchIndexContext.Provider>;
};

/**
 * The inlined index, or `undefined` on a page the server didn't render one for. Doubles as the
 * client's half of the SSR gate (`useIsPublicGallerySsr`): the server inlines this only when it
 * actually rendered with the index, so "the global is here" and "the server filtered by style"
 * are the same fact, and the two gates cannot disagree.
 */
export const useInitialThemeSearchIndex = (): PrecomputedEmbeddingsBundle | undefined => useContext(ThemeSearchIndexContext);
